import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { 
  PlusCircle, Edit, Trash2, Eye, Loader2, 
  Package, Truck, AlertCircle, Search, Filter as FilterIcon,
  ChevronRight, Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select'; 

// API & Components
import { getRequisitions, deleteRequisition } from '../../api/procurementApi'; 
import SidebarNav from '../../components/MasterSideBar';
import TopbarNav from "../../components/TopbarNav";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import RequisitionFormModal from '../../components/RequisitionFormModal';
import ViewRequisitionModal, { ViewRequisitionContent } from '../../components/programManager/ViewRequisitionModal';
import DeleteConfirmModal from '../../components/programManager/DeleteConfirmModal';

// Constants
const DEPARTMENT_OPTIONS = [
    { value: '', label: 'All Departments' },
    { value: 'operations', label: 'Operations' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'HR' },
    { value: 'it', label: 'IT' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'admin', label: 'Administration' },
];

const REQUISITION_STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending_director_approval', label: 'Pending Director' },
    { value: 'approved_by_director', label: 'Approved' },
    { value: 'rejected_by_director', label: 'Rejected' },
    { value: 'processing_by_procurement', label: 'In Procurement' },
    { value: 'completed', label: 'Completed' },
];

const PROCUREMENT_TYPE_FILTER_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'goods_services', label: 'Goods & Services' },
    { value: 'financial_request', label: 'Financial Request' },
    { value: 'inquiry_resolution', label: 'Inquiry' },
    { value: 'travel', label: 'Travel' },
    { value: 'contract', label: 'Contract' },
];

const ProcurementDashboard = () => {
    const navigate = useNavigate();
    
    // UI State
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data State
    const [requisitions, setRequisitions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(REQUISITION_STATUS_FILTER_OPTIONS[0]);
    const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState(DEPARTMENT_OPTIONS[0]);
    const [selectedTypeFilter, setSelectedTypeFilter] = useState(PROCUREMENT_TYPE_FILTER_OPTIONS[0]);

    // Modal State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [requisitionToDelete, setRequisitionToDelete] = useState(null);
    const [requisitionToEdit, setRequisitionToEdit] = useState(null);
    const [requisitionToView, setRequisitionToView] = useState(null);

    const fetchRequisitions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                status: selectedStatusFilter.value,
                department: selectedDepartmentFilter.value,
                typeOfProcurement: selectedTypeFilter.value,
                search: searchQuery,
            };
            const response = await getRequisitions(filters);
            const requisitionsData = response.data?.data || [];
            setRequisitions(requisitionsData);
        } catch (err) {
            setError("Failed to sync requisitions.");
            toast.error("Sync failed.");
            setRequisitions([]);
        } finally {
            setLoading(false);
        }
    }, [selectedStatusFilter, selectedDepartmentFilter, selectedTypeFilter, searchQuery]);

    useEffect(() => {
        fetchRequisitions();
    }, [fetchRequisitions]);

    const handleSaveSuccess = () => {
        fetchRequisitions();
        setIsCreateModalOpen(false);
        setRequisitionToEdit(null);
        toast.success("Record Synced Successfully");
    };

    const handleConfirmDelete = async () => {
        if (!requisitionToDelete) return;
        try {
            await deleteRequisition(requisitionToDelete._id);
            toast.success("Requisition Purged");
            fetchRequisitions();
        } catch (error) {
            toast.error("Deletion failed.");
        } finally {
            setIsDeleteModalOpen(false);
            setRequisitionToDelete(null);
        }
    };

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '1rem',
            padding: '0.25rem',
            border: '1px solid #f1f5f9',
            fontSize: '11px',
            fontWeight: '800',
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            boxShadow: 'none',
            '&:hover': { border: '1px solid #e2e8f0' }
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '11px',
            fontWeight: '800',
            textTransform: 'uppercase',
            backgroundColor: state.isSelected ? '#111827' : state.isFocused ? '#f8fafc' : 'transparent',
            color: state.isSelected ? 'white' : '#64748b',
        })
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); navigate("/"); }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchQuery} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto">
                        
                        <div className="flex flex-col items-center text-center mb-12">
                            <PremiumHeader 
                                title="Procurement" 
                                lastWordAccent="Management" 
                                subtitle="Manage resource requests, supply chain approvals, and departmental requisitions." 
                            />
                            <div className="flex flex-wrap justify-center gap-4 mt-8">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => { setRequisitionToEdit(null); setIsCreateModalOpen(true); }}
                                    className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
                                >
                                    <PlusCircle size={16} strokeWidth={3} /> Initialize Requisition
                                </motion.button>
                            </div>
                        </div>

                        {/* Stats Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                                <PremiumStatCard 
                                    title="Active Submissions" 
                                    value={requisitions.length} 
                                    icon={<Package className="text-orange-600" />} 
                                    iconBg="bg-orange-50"
                                    description="Current volume"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                                <PremiumStatCard 
                                    title="Pipeline Status" 
                                    value={requisitions.filter(r => r.currentStatus.includes('pending') || r.currentStatus === 'submitted').length} 
                                    icon={<Truck className="text-blue-600" />} 
                                    iconBg="bg-blue-50"
                                    description="In-transit assets"
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                                <PremiumStatCard 
                                    title="Exception Queue" 
                                    value={requisitions.filter(r => r.currentStatus === 'rejected_by_director').length} 
                                    icon={<AlertCircle className="text-red-600" />} 
                                    iconBg="bg-red-50"
                                    description="Action required"
                                />
                            </motion.div>
                        </div>

                        {/* Filters & Content */}
                        <div className="space-y-8">
                            <PremiumCard title="Strategic Sourcing Filters" icon={<FilterIcon size={18} />}>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-2">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Administrative Status</label>
                                        <Select 
                                            options={REQUISITION_STATUS_FILTER_OPTIONS} 
                                            value={selectedStatusFilter} 
                                            onChange={setSelectedStatusFilter} 
                                            styles={selectStyles}
                                            isSearchable={false}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Business Unit</label>
                                        <Select 
                                            options={DEPARTMENT_OPTIONS} 
                                            value={selectedDepartmentFilter} 
                                            onChange={setSelectedDepartmentFilter} 
                                            styles={selectStyles}
                                            isSearchable={false}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Category Type</label>
                                        <Select 
                                            options={PROCUREMENT_TYPE_FILTER_OPTIONS} 
                                            value={selectedTypeFilter} 
                                            onChange={setSelectedTypeFilter} 
                                            styles={selectStyles}
                                            isSearchable={false}
                                        />
                                    </div>
                                </div>
                            </PremiumCard>

                            <PremiumCard title="Purchase Requisitions Journal" icon={<Search size={18} />}>
                                <div className="overflow-x-auto">
                                    {loading ? (
                                        <div className="py-24 flex flex-col items-center justify-center">
                                            <Loader2 className="size-10 animate-spin text-orange-500 mb-6" />
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Synchronizing Data Ledger...</p>
                                        </div>
                                    ) : requisitions.length === 0 ? (
                                        <div className="py-24 text-center">
                                            <Package size={48} className="mx-auto text-gray-100 mb-6" strokeWidth={1} />
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No matching records found</p>
                                        </div>
                                    ) : (
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="bg-gray-50/50">
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Identity & Reference</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Category</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Priority</th>
                                                    <th className="px-8 py-5 text-right"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {requisitions.map((req, i) => (
                                                    <motion.tr 
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: i * 0.05 }}
                                                        key={req._id} 
                                                        className="hover:bg-gray-50/50 transition-all group"
                                                    >
                                                        <td className="px-8 py-6">
                                                            <div className="flex flex-col">
                                                                <span className="font-black text-xs text-gray-800 uppercase tracking-tight group-hover:text-orange-600 transition-colors">{req.title}</span>
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 flex items-center gap-2">
                                                                    <ChevronRight size={10} className="text-orange-500" />
                                                                    {req.projectId?.projectTitle || 'General Expense'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <div className="flex items-center gap-3">
                                                                <div className="size-1.5 rounded-full bg-orange-200"></div>
                                                                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                                                                    {req.typeOfProcurement.replace(/_/g, ' ')}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <span className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest ${
                                                                req.currentStatus === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                                                req.currentStatus.includes('rejected') ? 'bg-red-50 text-red-600' :
                                                                'bg-blue-50 text-blue-600'
                                                            }`}>
                                                                {req.currentStatus.replace(/_/g, ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="px-8 py-6">
                                                            <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2 w-fit ${
                                                                req.priority === 'urgent' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
                                                            }`}>
                                                                {req.priority === 'urgent' && <AlertCircle size={10} />}
                                                                {req.priority}
                                                            </span>
                                                        </td>
                                                        <td className="px-8 py-6 text-right">
                                                            <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all">
                                                                <button onClick={() => { setRequisitionToView(req); setIsViewModalOpen(true); }} className="p-3 bg-white text-gray-400 rounded-xl hover:bg-orange-50 hover:text-orange-600 transition-all shadow-sm border border-gray-100"><Eye size={16} strokeWidth={3}/></button>
                                                                <button onClick={() => { setRequisitionToEdit(req); setIsCreateModalOpen(true); }} className="p-3 bg-white text-gray-400 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all shadow-sm border border-gray-100"><Edit size={16} strokeWidth={3}/></button>
                                                                <button onClick={() => { setRequisitionToDelete(req); setIsDeleteModalOpen(true); }} className="p-3 bg-white text-gray-400 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all shadow-sm border border-gray-100"><Trash2 size={16} strokeWidth={3}/></button>
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </PremiumCard>
                        </div>
                    </div>
                </main>
            </div>

            <RequisitionFormModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSaveSuccess={handleSaveSuccess} requisitionToEdit={requisitionToEdit} />
            
            <AnimatePresence>
                {isViewModalOpen && requisitionToView && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }} 
                            onClick={() => setIsViewModalOpen(false)} 
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.95, opacity: 0, y: 20 }} 
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-100"
                        >
                            <div className="p-0 overflow-y-auto max-h-full">
                                <ViewRequisitionContent
                                    requisition={requisitionToView}
                                    onRequestClose={() => setIsViewModalOpen(false)}
                                />
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <DeleteConfirmModal isOpen={isDeleteModalOpen} onRequestClose={() => setIsDeleteModalOpen(false)} onConfirm={handleConfirmDelete} itemType="requisition" />
        </div>
    );
};

export default ProcurementDashboard;